import { EventType } from "@/analytics/analytics-events";
import {
  FilterAnnotationListEventProperties,
  SortAnnotationListEventProperties,
} from "@/analytics/annotation-events-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { getAiDetectedTag } from "@/store/ai-asset-review/ai-asset-review-data-selectors";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import {
  selectAllProjectIdsForIntegrations,
  selectWorkspaceIntegrations,
} from "@/store/integrations/integrations-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCanUseObjectDetection } from "@/store/subscription-features/subscription-features-selectors";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElement,
  IElementType,
  isIElementMarkup,
} from "@faro-lotv/ielement-types";
import {
  // At project loading time, we load all the annotations iElements. The lightweight
  // elements are being all fetched separately.
  // eslint-disable-next-line no-restricted-imports
  addIElements,
} from "@faro-lotv/project-source";
import {
  GetTopicResponse,
  ProjectApiError,
  ResponseSize,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState, type JSX } from "react";
import {
  getBcfIntegrationTypeForIElementType,
  getWorkspaceIntegrationProviderId,
} from "../annotations/external-annotation-utils";
import {
  AiAnnotationStatus,
  AiStatusFilter,
} from "../filter-panel/filters/ai-status-filter";
import { AssigneeFilter } from "../filter-panel/filters/assignee-filter";
import { DateFilter, Dates } from "../filter-panel/filters/date-filter";
import { StatusFilter } from "../filter-panel/filters/status-filter";
import { TagFilter } from "../filter-panel/filters/tag-filter";
import { SearchFilterSortPanel } from "../filter-panel/search-filter-sort-panel";
import { AnnotationSortingOptions } from "../filter-panel/sort-menu-button";
import { AiReviewNotification } from "./ai-notification/ai-review-notification";
import { AnnotationList } from "./annotation-list";
import {
  AnnotationFilteringOptions,
  ExternalMarkupMetadata,
  filterExternalMarkup,
  HybridAnnotationStatusKey,
  MarkupMetadata,
  selectExternalMarkupElements,
  selectFilteredAndSortedSphereXgMarkups,
  sortMarkupMetadata,
} from "./annotation-panel-selector";

export type AiMarkupData = {
  aiDetectedTag?: string;
};

const MARKUPS_PER_PAGE = 1000;

/**
 * Fetches all markups of the current project, regardless of the currently active area.
 *
 * @returns true if more data are still being fetched
 */
function useFetchAllMarkups(): boolean {
  const { projectApiClient: client } = useApiClientContext();

  const { handleErrorWithDialog } = useErrorHandlers();

  const { getState } = useAppStore();

  // The 'markupsLoading' state is initialized to 'true' because
  // initially the fetching still has to be performed, and the GUI
  // should already show convenient visual cues such as skeletons.
  const [markupsLoading, setMarkupsLoading] = useState(true);

  const dispatch = useAppDispatch();

  useEffect(() => {
    setMarkupsLoading(true);
    const ac = new AbortController();

    // Callback executed when a new page of markups is fetched
    async function nextPageFetched(
      _: number,
      result: IElement[],
    ): Promise<void> {
      if (ac.signal.aborted) {
        return Promise.resolve();
      }

      // Fetch the children of the markups, so that all infos are available
      const markups = result.filter(isIElementMarkup);

      // Check which elements need to be fetched, given the newly fetched markups
      dispatch(addIElements(result));

      let missingChildren = selectMissingDescendants(getState(), markups);
      while (missingChildren.length > 0) {
        const elements = await client.getAllIElements({
          ids: missingChildren,
          // Since the previous request has a small batch size, the markups children should not exceed the length of a single page here.
          expectedResponseSize: ResponseSize.small,
        });
        dispatch(addIElements(elements));
        missingChildren = selectMissingDescendants(getState(), elements);
      }
    }

    // When the app starts and this annotation panel is created,
    // all markups of the project are fetched and loaded into the panel,
    // regardless of the currently active area.
    client
      .getAllIElements({
        signal: ac.signal,
        types: [
          IElementType.markup,
          IElementType.markupBim360,
          IElementType.markupAccIssue,
          IElementType.markupAccRfi,
          IElementType.markupProcoreObservation,
          IElementType.markupProcoreRfi,
        ],
        itemsPerPage: MARKUPS_PER_PAGE,
        onNextPageFetched: nextPageFetched,
      })
      .catch((e: ProjectApiError) => {
        if (ac.signal.aborted) {
          return;
        }
        handleErrorWithDialog({
          title: "Error while fetching all annotations",
          error: e,
        });
      })
      .finally(() => {
        // Do not set markups loading to false if the fetch was aborted
        // to not have a flashing loading notification when react re-mount the node
        if (!ac.signal.aborted) {
          setMarkupsLoading(false);
        }
      });

    return () => ac.abort();
  }, [client, dispatch, getState, handleErrorWithDialog]);

  return markupsLoading;
}

/** @returns the Panel to search and interact with all the project annotations */
export function AnnotationPanel(): JSX.Element {
  const [assignee, setAssignee] = useState<GUID[]>([]);
  const [tags, setTags] = useState<GUID[]>([]);
  const [statusId, setStatusId] = useState<HybridAnnotationStatusKey[]>([]);
  const [date, setDate] = useState<Dates>();
  const [sorting, setSorting] = useState<AnnotationSortingOptions>();
  const [searchText, setSearchText] = useState<string>("");
  const [aiStatus, setAiStatus] = useState<AiAnnotationStatus[]>([]);
  const [externalMarkupMetadata, setExternalMarkupMetadata] =
    useState<ExternalMarkupMetadata[]>();

  const canUseObjectDetection = useAppSelector(selectCanUseObjectDetection);
  const filteringOptions = useMemo<AnnotationFilteringOptions>(
    () => ({
      assignee,
      tags,
      statusId,
      date,
      sorting,
      searchText,
      aiStatus,
    }),
    [assignee, date, sorting, statusId, tags, searchText, aiStatus],
  );

  const sphereXgMarkupsLoading = useFetchAllMarkups();
  const externalMarkupsLoading = useMemo(
    () => externalMarkupMetadata === undefined,
    [externalMarkupMetadata],
  );
  const { bcfServicesApiClient } = useApiClientContext();
  const filteredSphereXgMarkupData = useAppSelector((state) =>
    selectFilteredAndSortedSphereXgMarkups(state, filteringOptions),
  );
  const externalMarkupElements = useAppSelector(selectExternalMarkupElements);
  const allProjectIdsForIntegrations = useAppSelector(
    selectAllProjectIdsForIntegrations,
  );
  const workspaceIntegrations = useAppSelector(selectWorkspaceIntegrations);

  useEffect(() => {
    async function getExternalMarkupsData(): Promise<void> {
      const externalMarkupsData: ExternalMarkupMetadata[] = [];
      for (const element of externalMarkupElements) {
        const integrationType = getBcfIntegrationTypeForIElementType(
          element.type,
        );
        const integrationProjectId =
          allProjectIdsForIntegrations[integrationType];
        const projectIntegrationEnabled = integrationProjectId !== undefined;
        const workspaceIntegrationEnabled = workspaceIntegrations.includes(
          getWorkspaceIntegrationProviderId(integrationType),
        );
        if (!projectIntegrationEnabled || !workspaceIntegrationEnabled) {
          // We are not reporting any error or message for now
          // This case will be handled by https://faro01.atlassian.net/browse/CADBIM-1450
          continue;
        }
        try {
          const topic = await bcfServicesApiClient.getTopic(
            integrationType,
            integrationProjectId,
            element.externalIssueId,
          );
          externalMarkupsData.push({
            markup: element,
            title: topic.title,
            assigneeName: topic.assigned_to?.name ?? undefined,
            statusId: topic.topic_status?.id ?? undefined,
            dueDate: topic.due_date ?? undefined,
            fetchedData: topic,
          });
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (error) {
          // We are not reporting any error or message for now
          // This case will be handled by https://faro01.atlassian.net/browse/CADBIM-1450
          continue;
        }
      }
      setExternalMarkupMetadata(externalMarkupsData);
    }
    setExternalMarkupMetadata(undefined);
    getExternalMarkupsData();
  }, [
    externalMarkupElements,
    bcfServicesApiClient,
    allProjectIdsForIntegrations,
    workspaceIntegrations,
  ]);

  useEffect(() => {}, [
    externalMarkupMetadata,
    filteredSphereXgMarkupData,
    filteringOptions,
  ]);

  const allMarkupIds = useMemo(() => {
    const filteredExternalMarkupData: MarkupMetadata[] = [];
    for (const element of externalMarkupMetadata ?? []) {
      const filteredMarkup = filterExternalMarkup(element, filteringOptions);
      if (filteredMarkup) {
        filteredExternalMarkupData.push(filteredMarkup);
      }
    }

    const allFilteredMarkups = [
      ...filteredSphereXgMarkupData,
      ...filteredExternalMarkupData,
    ];
    const markups = sortMarkupMetadata(
      allFilteredMarkups,
      filteringOptions.sorting,
    );
    return markups.map((markup) => markup.markup.id);
  }, [externalMarkupMetadata, filteredSphereXgMarkupData, filteringOptions]);

  const allExternalMarkupData: Record<GUID, GetTopicResponse> = useMemo(() => {
    const data: Record<GUID, GetTopicResponse> = {};
    for (const element of externalMarkupMetadata ?? []) {
      data[element.markup.id] = element.fetchedData;
    }
    return data;
  }, [externalMarkupMetadata]);

  const allAiMarkupData: Record<GUID, AiMarkupData> = useMemo(
    () =>
      filteredSphereXgMarkupData.reduce<Record<GUID, AiMarkupData>>(
        (data, { markup }) => {
          data[markup.id] = {
            aiDetectedTag: getAiDetectedTag(markup),
          };
          return data;
        },
        {},
      ),
    [filteredSphereXgMarkupData],
  );

  useTrackFilteringOptions(filteringOptions, canUseObjectDetection);

  return (
    <Stack sx={{ m: 0, width: "100%", height: "100%", overflow: "hidden" }}>
      <SearchFilterSortPanel
        hasFilters={
          assignee.length > 0 ||
          tags.length > 0 ||
          statusId.length > 0 ||
          !!date ||
          aiStatus.length > 0
        }
        onSortingChanged={setSorting}
        text={searchText}
        onTextChanged={setSearchText}
      >
        <AssigneeFilter
          selectedAssigneeIds={assignee}
          onSelectedAssigneesChanged={setAssignee}
        />
        <TagFilter selectedTags={tags} onSelectedTagsChanged={setTags} />
        <StatusFilter
          selectedStatus={statusId}
          onSelectedStatusChanged={setStatusId}
        />
        <DateFilter selectedDate={date} onSelectedDateChanged={setDate} />
        {canUseObjectDetection && (
          <AiStatusFilter
            selectedAiStatus={aiStatus}
            onSelectedAiStatusChanged={setAiStatus}
          />
        )}
      </SearchFilterSortPanel>

      <AiReviewNotification />
      <AnnotationList
        markupIds={allMarkupIds}
        externalMarkupsData={allExternalMarkupData}
        aiMarkupsData={allAiMarkupData}
        markupsLoading={sphereXgMarkupsLoading || externalMarkupsLoading}
      />
    </Stack>
  );
}

function useTrackFilteringOptions(
  filteringOptions: AnnotationFilteringOptions,
  canUseObjectDetection: boolean,
): void {
  const hasSearchFilter = !!filteringOptions.searchText;
  const hasUserFilter = filteringOptions.assignee.length > 0;
  const hasTagsFilter = filteringOptions.tags.length > 0;
  const hasStatusFilter = filteringOptions.statusId.length > 0;
  const hasDueDateFilter = !!filteringOptions.date;
  const hasAiStatusFilter =
    canUseObjectDetection && filteringOptions.aiStatus.length > 0;

  // Track filtering changes
  useEffect(() => {
    if (
      hasSearchFilter ||
      hasUserFilter ||
      hasTagsFilter ||
      hasStatusFilter ||
      hasDueDateFilter ||
      hasAiStatusFilter
    ) {
      Analytics.track<FilterAnnotationListEventProperties>(
        EventType.sortAnnotationList,
        {
          hasSearchFilter,
          hasUserFilter,
          hasTagsFilter,
          hasStatusFilter,
          hasDueDateFilter,
          hasAiStatusFilter,
        },
      );
    }
  }, [
    hasDueDateFilter,
    hasSearchFilter,
    hasStatusFilter,
    hasTagsFilter,
    hasUserFilter,
    hasAiStatusFilter,
  ]);

  // Track sorting changes
  useEffect(() => {
    if (filteringOptions.sorting) {
      Analytics.track<SortAnnotationListEventProperties>(
        EventType.sortAnnotationList,
        {
          sortBy: filteringOptions.sorting,
        },
      );
    }
  }, [filteringOptions.sorting]);
}

/**
 * @returns The ids of the first missing descendants of the input elements
 * @param iElements The iElements store
 * @param elements The list of iElements for which we want to download the missing
 * descendants
 */
const selectMissingDescendants = createSelector(
  [
    selectIElementsSlice,
    (iElements: OnlyIElementsStore, elements: IElement[]) => elements,
  ],
  ({ iElements }, elements) => {
    const toLoad: GUID[] = [];
    const stack = [...elements];
    while (stack.length > 0) {
      const e = stack.pop();
      if (!e) break;

      if (e.childrenIds) {
        for (const childId of e.childrenIds) {
          const child = iElements[childId];
          if (child) {
            stack.push(child);
          } else {
            toLoad.push(childId);
          }
        }
      }
    }
    return toLoad;
  },
);
